import type { Module } from "vuex";

import type { DefaultApi } from "@/api";
import { RoiMetrics, type RoiMetricsSettings } from "@/models/roi_metrics";
import { StatisticsDateFilters } from "@/models/statistics";
import type { State as RootState } from "@/stores";

export interface State {
  selectedPeriod: StatisticsDateFilters;
  // Org-wide settings from GET /roi-metrics-settings/; null when unloaded or no member has saved them.
  settings: RoiMetricsSettings | null;
}

export const module: Module<State, RootState> = {
  namespaced: true,

  state: () => ({
    selectedPeriod: StatisticsDateFilters.this_year,
    settings: null,
  }),

  getters: {
    // Falls back to defaults so charts can render; isSetupComplete distinguishes saved from default.
    settings: (state) => state.settings ?? RoiMetrics.getDefaultSettings(),
    isSetupComplete: (state) => state.settings !== null,
  },

  actions: {
    setSelectedPeriod({ commit }, period: StatisticsDateFilters) {
      commit("setSelectedPeriod", period);
    },
    async fetchRoiMetricsSettings({ commit, dispatch }) {
      const api: DefaultApi = await dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.getRoiSettings();
      commit("setSettings", RoiMetrics.settingsFromResponse(response.data));
    },
    async saveRoiMetricsSettings(
      { commit, dispatch },
      settings: RoiMetricsSettings,
    ) {
      const api: DefaultApi = await dispatch("getAuthorizedApi", null, {
        root: true,
      });
      const response = await api.setRoiSettings(
        RoiMetrics.toSettingsRequest(settings),
      );
      commit("setSettings", RoiMetrics.settingsFromResponse(response.data));
    },
  },

  mutations: {
    setSelectedPeriod(state, period: StatisticsDateFilters) {
      state.selectedPeriod = period;
    },
    setSettings(state, settings: RoiMetricsSettings | null) {
      state.settings = settings;
    },
  },
};
